import {buildStandardHeaders, otsBaseApi} from "../../ots-api";
import {ContainerStatus} from "../../../model/container-status";
import {removeUndefined} from "../../../util/util";
import {LevelEntity} from "../../../model/level";
import {BaseQueryApi} from "@reduxjs/toolkit/query";
import {OtsAsyncThunkConfigExtra} from "../../ots-store";
import {ContainerLevelHistory, ContainerLevelHistorySearchCriteria} from "../../../model/container-level-history";
import {ContainerLevelHistoryHolder} from "../../../model/container-level-history-holder";

const taggedBaseApi = otsBaseApi.enhanceEndpoints({addTagTypes: ["ContainerLevels", "ContainerStatuses", "TicketContainerLevels"]});

type ContainerStatusArg = {
  regionId: string
  category: string
  assignedOnly?: boolean
  siteCompanyIds?: string[]
  containerIds?: string[]
  pageRequest?: {
    limit: number
    lastExternalId?: string
    lastId?: string
  }
}

type ContainerLevelArg = {
  containerId: string
  regionId: string
  cargoSubtype: string
  type: string
}

export const containerLevelApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllContainerStatuses: builder.query<ContainerStatus[], ContainerStatusArg>({
      query: (arg) => ({
        url: `/containerlevelhistory/container-status/search`,
        method: "POST",
        body: removeUndefined({
          regionIds: [arg.regionId],
          category: arg.category,
          assignedOnly: arg.assignedOnly,
          siteCompanyIds: arg.siteCompanyIds,
          containerIds: arg.containerIds,
          pageRequest: arg.pageRequest,
        })
      }),
      providesTags: (result, _error, arg, _meta) => {
        if (result) {
          return [
            {type: 'ContainerStatuses', id: arg.pageRequest ? 'PARTIAL-LIST' : 'LIST'},
          ];
        } else {
          return [{type: 'ContainerStatuses', id: arg.pageRequest ? 'PARTIAL-LIST' : 'LIST'}];
        }
      },
    }),
    getContainerStatus: builder.query<ContainerStatus, {
      containerId: string,
      regionId: string
    }>({
      query: ({containerId, regionId}) => ({
        url: `/containerlevelhistory/container-status/${containerId}`,
        method: "GET",
        params: {
          regionId: regionId,
          withLevels: true,
        }
      }),
      providesTags: (result, error, {containerId}) => [{type: 'ContainerStatuses', containerId}],
    }),
    getLatestContainerLevelsByContainer: builder.query<LevelEntity[], ContainerLevelArg>({
      query: (arg) => ({
        url: `/containerlevelhistory/latest/${arg.containerId}`,
        method: "GET",
        params: removeUndefined({
          regionId: arg.regionId,
          cargoSubtype: arg.cargoSubtype,
          type: arg.type,
        })
      }),
      providesTags: (result, _error, arg, _meta) => {
        return [{type: 'ContainerLevels', id: arg.containerId}]
      },
    }),
    searchContainerLevels: builder.query<ContainerLevelHistory[], ContainerLevelHistorySearchCriteria>({
      query: (arg) => ({
        url: `/containerlevelhistory/searchcriteria`,
        method: "POST",
        body: arg
      }),
    }),
    searchTicketContainerLevels: builder.query<ContainerLevelHistoryHolder, { id: string, criteria: ContainerLevelHistorySearchCriteria }>({
      query: ({criteria}) => ({
        url: `/containerlevelhistory/searchcriteria/withtransport`,
        method: "POST",
        body: criteria
      }),
      providesTags: (result, _error, arg, _meta) => {
        return [{type: 'TicketContainerLevels', id: arg.id}]
      },
    }),
  })
});

export const downloadContainerStatusReport = (arg: ContainerStatusArg, api: BaseQueryApi): Promise<Response> => {
  const headers = buildStandardHeaders(api)
  headers["Accept"] = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  headers["Content-Type"] = 'application/json'

  const request: RequestInit = {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(
      removeUndefined({
        regionIds: [arg.regionId],
        category: arg.category,
        assignedOnly: arg.assignedOnly,
        siteCompanyIds: arg.siteCompanyIds,
        containerIds: arg.containerIds,
      })
    ),
  }

  const baseUrl = (api.extra as OtsAsyncThunkConfigExtra).baseUrl;
  const url = `${baseUrl}/containerlevelhistory/container-status-report`;

  return window.fetch(url, request)
    .then((response) => {
      if (response.ok) {
        return Promise.resolve(response)
      } else {
        return Promise.reject(response)
      }
    });
}

export const {
  useGetAllContainerStatusesQuery,
  useLazyGetAllContainerStatusesQuery,
  useGetContainerStatusQuery,
  useGetLatestContainerLevelsByContainerQuery,
  useSearchContainerLevelsQuery,
  useSearchTicketContainerLevelsQuery,
} = containerLevelApi;
